import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { Button, Checkbox, FormControl, FormControlLabel, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";

import InfoIcon from '@mui/icons-material/Info';

import Header from "../../Header";
import { api } from "../../../utils";
import { SalesmanModel } from "../Commission";
import { IError } from "../../../models/ReportPageModels";
import CommissionPayRollItem from "../CommissionPayRollItem";
import cloneDeep from "lodash.clonedeep";

interface GetSalesmenRequest {
    data: GetSalesmenData;
    error: IError;
}
interface GetSalesmenData {
    salesmen: SalesmanModel[];
}

interface CommissionPayRollRequestModel {
    client: string;
    showPaid: boolean;
    showXeroUnpaid: boolean;
}

interface CommissionPayRollResponse {
    data: CommissionPayRollData;
    error: IError;
}
interface CommissionPayRollData {
    commissionPayRolls: CommissionPayRollModel[];
}
export interface CommissionPayRollModel {
    id: number;
    salesman: string;
    company: string;
    reportDate: string;
    isPaid: number;
    amount: number;
    paymentDateStr: string;
    xeroInvoiceStatuses: PayRollInvoiceStatusModel[];
}

interface PayRollInvoiceStatusModel {
    id: string;
    number: string;
    status: string;
    invoicePayDate: string;
}

interface SaveCommissionPayRollRequestModel {
    commissionPayRolls: CommissionPayRollModel[];
}

function CommissionPayRoll() {
    const { enqueueSnackbar } = useSnackbar();

    const [salesmen, setSalesmen] = useState<SalesmanModel[]>([]);
    const [selectedSalesman, setSelectedSalesman] = useState<string>("");

    const [showPaid, setShowPaid] = useState<boolean>(false);
    const [showXeroUnpaid, setShowXeroUnpaid] = useState<boolean>(false);

    const [commissionPayRolls, setCommissionPayRolls] = useState<CommissionPayRollModel[]>([]);

    const [payRollsMessage, setPayRollsMessage] = useState<string>("");

    const [payRollsTotal, setPayRollsTotal] = useState<number>(0.0);

    useEffect(() => {
        GetSalesmen();
    }, [])

    const GetSalesmen = async () => {
        try {
            const response: GetSalesmenRequest = await api.get("api/Commission/GetSalesmen", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setSalesmen(response.data.salesmen)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const LoadPayroll = async () => {
        if (selectedSalesman === "") {
            enqueueSnackbar("Please select the salesman", { variant: "error" });
            return;
        }

        setPayRollsMessage("");
        setPayRollsTotal(0.0);
        setCommissionPayRolls([]);

        var reqBody: CommissionPayRollRequestModel = {
            client: selectedSalesman,
            showPaid: showPaid,
            showXeroUnpaid: showXeroUnpaid
        };

        try {
            const response: CommissionPayRollResponse = await api.post("api/Commission/CommissionPayRoll", {
                json: reqBody,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCommissionPayRolls(response.data.commissionPayRolls);

                if (response.data.commissionPayRolls.length == 0)
                    setPayRollsMessage("No payrolls found")

                CalculateTotal(response.data.commissionPayRolls);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const SavePayroll = async () => {
        setPayRollsTotal(0.0);

        var reqBody: SaveCommissionPayRollRequestModel = {
            commissionPayRolls: commissionPayRolls
        }

        try {
            const response: CommissionPayRollResponse = await api.post("api/Commission/SaveCommissionPayRoll", {
                json: reqBody,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCommissionPayRolls(response.data.commissionPayRolls);

                CalculateTotal(response.data.commissionPayRolls);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function CalculateTotal(payRolls: CommissionPayRollModel[]) {
        var total = payRolls.filter(x => x.isPaid == 0).reduce((sum, item) => sum + item.amount, 0);
        setPayRollsTotal(total);
    }

    const CheckAll = async () => {
        const tempList: CommissionPayRollModel[] = [];

        for (var i = 0; i < commissionPayRolls.length; i++) {
            const temp: CommissionPayRollModel = cloneDeep(commissionPayRolls[i]);
            if (commissionPayRolls[i].paymentDateStr == null) {
                temp.isPaid = 1;
            }
            tempList.push(temp)
        }

        setCommissionPayRolls(tempList);
    }

    function CopyToClipBoard() {
        const result: string[] = [];
        for (let i = 0; i < commissionPayRolls.length; i++) {
            const item = commissionPayRolls[i];
            var temp: string = "";
            temp += item.salesman + '\t';
            temp += item.company + '\t';
            temp += item.reportDate + '\t';
            temp += '£' + item.amount + '\t';

            if (item.xeroInvoiceStatuses.some(x => x.status === "PAID")) {
                const paid: PayRollInvoiceStatusModel = item.xeroInvoiceStatuses.find(x => x.status == "PAID")!;

                temp += paid.number + '\t';
                temp += paid.invoicePayDate;
            }
            else if (item.xeroInvoiceStatuses.some(x => x.status !== "DELETED" && x.status !== "VOIDED")) {
                const paid: PayRollInvoiceStatusModel = item.xeroInvoiceStatuses.find(x => x.status !== "DELETED" && x.status !== "VOIDED")!;

                temp += paid.number + "\t\t";
            }
            else if (item.xeroInvoiceStatuses.length !== 0) {
                const paid: PayRollInvoiceStatusModel = item.xeroInvoiceStatuses[0];

                temp += paid.number + "\t\t";
            }
            else {
                temp += "No invoice found\t\t";
            }

            result.push(temp);
        }

        navigator.clipboard.writeText(result.join('\n'))
        enqueueSnackbar("Copied!", { variant: "success" });
    }

    return (
        <>
            <Header headerType={"PAYROLL"} displayLogout={false} />
            <Stack direction='row'>
                <Stack
                    direction="column"
                    sx={{ width: '320px' }}
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={1.5}>

                    <p>Salesmen</p>
                    <FormControl sx={{ width: "240px" }}>
                        <Select
                            value={selectedSalesman}
                            onChange={(newValue) => { setSelectedSalesman(newValue.target.value); }}
                            size="small"
                            defaultValue={"All"}>
                            <MenuItem value={"All"} key={0}>All</MenuItem>
                            {salesmen.map((item, index) => (
                                <MenuItem value={item.sales} key={index + 1}>{item.sales}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <br />
                    <Tooltip
                        placement="right"
                        title={
                            <Typography
                                fontSize={"16px"}
                                sx={{ whiteSpace: "pre-line" }}>
                                (This will display client invoices that have been paid, but that have commission due which has not been submitted to payroll or passed to the 3rd party sales person)
                            </Typography>
                        }>
                        <Stack
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            spacing={1}>
                            <p>Show outstanding commission due</p>
                            <InfoIcon />
                        </Stack>
                    </Tooltip>

                    <FormControlLabel
                        label="Include paid commissions"
                        control={
                            <Checkbox
                                sx={{ marginLeft: '15px', height: '20px' }}
                                checked={showPaid}
                                onChange={(e) => { setShowPaid(e.target.checked) }}
                            />
                        } />

                    <FormControlLabel
                        label="Show Xero unpaid invoices"
                        control={
                            <Checkbox
                                sx={{ marginLeft: '15px', height: '20px' }}
                                checked={showXeroUnpaid}
                                onChange={(e) => { setShowXeroUnpaid(e.target.checked) }}
                            />
                        } />

                    <Button
                        variant="outlined"
                        onClick={LoadPayroll}
                        sx={{ left: '54px' }}>
                        View PayRoll
                    </Button>
                </Stack>
                <Stack
                    alignItems={"start"}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: "100%" }}>
                        <Button
                            variant="outlined"
                            onClick={SavePayroll}
                            sx={{ marginLeft: "10px" }}>
                            Save PayRolls
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={CopyToClipBoard}>
                            Copy to spreadsheet (in clipboard)
                        </Button>
                    </Stack>

                    {payRollsMessage}

                    <Stack sx={{ overflowY: "auto", height: "700px", width: "auto" }}>
                        {commissionPayRolls.map((payRoll) =>
                            <CommissionPayRollItem
                                key={payRoll.id}
                                payRoll={payRoll} />
                        )}
                    </Stack>

                    <Stack
                        direction={"row"}
                        justifyContent="flex-end"
                        alignItems="center"
                        spacing={3}
                        sx={{ width: "100%" }}>
                        <p style={{ fontSize: "20px" }}>Total of unpaid: {payRollsTotal.toFixed(2)}</p>

                        <Button
                            variant="outlined"
                            onClick={CheckAll}
                            sx={{ height: "40px" }}>
                            Check all
                        </Button>
                    </Stack>
                </Stack>

            </Stack>
        </>
    )
}

export default CommissionPayRoll;